import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Events } from "ionic-angular";
import { Observable } from "rxjs";
import { map, mapTo, tap } from "rxjs/operators";
import { ConfigurationService } from "../configuration/configuration.service";
import { FeatureToggle } from "../state-management/state-management.service";

export interface FeatureToggleDetails {
  id: string;
  featureId: string;
  createdBy: string;
  updatedBy: string;
  createdDatetime: string;
  updatedDatetime: string;
  startDatetime: string;
  endDatetime: string;
  isActive: boolean;
  tenantId: number;
  featureName: string;
}

@Injectable()
export class FeatureToggleDataService {
  constructor(
    private http: HttpClient,
    private config: ConfigurationService,
    public events: Events
  ) {}

  private getFeatureToggleUrl() {
    if (this.config.getConfig().isLocal) {
      return this.config.getConfig().localEndpoints.featureToggleUrl;
    } else {
      return this.config.getConfig().apiUrl + "/feature-toggles";
    }
  }

  private featuresWithOverrideMapper = (featureToggles: FeatureToggle[]) => {
    const { featureToggleOverrides } = this.config.getConfig();
    if (!featureToggleOverrides) {
      return featureToggles;
    }
    if (!featureToggles || featureToggles.length === 0) {
      return [];
    }
    // check feature override map and replace active flag if required
    return featureToggles.map((featureToggle) => {
      const featureToggleAttribute = featureToggle.name.toLocaleLowerCase();
      if (featureToggleOverrides[featureToggleAttribute] !== undefined) {
        featureToggle.active = featureToggleOverrides[featureToggleAttribute];
      }
      return featureToggle;
    });
  };

  public getFeatureToggles() {
    return this.http
      .get<FeatureToggle[]>(this.getFeatureToggleUrl())
      .pipe(map(this.featuresWithOverrideMapper));
  }

  public loadToggles(): Observable<void> {
    return this.http.get<FeatureToggle[]>(this.getFeatureToggleUrl()).pipe(
      map(this.featuresWithOverrideMapper),
      tap((x: FeatureToggle[]) => {
        if (x && x.length) {
          x.forEach((y: FeatureToggle) => {
            localStorage.setItem(`ft-${y.name}`, `${y.active}`);
          });
        } else {
          console.error("could not find any feature toggle for selected org");
        }
      }),
      mapTo(void 0) // Emit 'void' once the task is complete
    );
  }
  public getFeatureToggleDetails(): Observable<FeatureToggleDetails[]> {
    return this.http.get<FeatureToggleDetails[]>(this.getFeatureToggleUrl(), {
      params: new HttpParams({
        fromObject: {
          s: "0",
        },
      }),
    });
  }

  public getToggleValue(toggle: string): boolean {
    const toggleString = localStorage.getItem(`ft-${toggle}`);
    if (toggleString === "true") {
      return true;
    }

    return false;
  }
  public getAllStoredToggles(): FeatureToggle[] {
    const storedToggles: FeatureToggle[] = [];
    // Iterate over all keys in localStorage
    Object.keys(localStorage).forEach((key) => {
      // Check if the key corresponds to a stored toggle
      if (key.startsWith("ft-")) {
        const toggleName = key.substring(3); // Remove the 'ft-' prefix
        const toggleValueString = localStorage.getItem(key);
        // Convert toggle value to boolean
        const toggleValue = toggleValueString === "true";
        // Add the toggle to the array if it's active (active: true) s
        const toggle: FeatureToggle = {
          name: toggleName,
          active: toggleValue,
        };
        storedToggles.push(toggle);
      }
    });
    return storedToggles;
  }

  public putToggle(toggle: string, newState: boolean) {
    return this.http.put(`${this.getFeatureToggleUrl()}/${toggle}`, {
      isActive: newState,
    });
  }
}



// WEBPACK FOOTER //
// ./src/providers/features-service/feature-toggle.data.service.ts